import { createFileRoute, notFound } from "@tanstack/react-router";
import { CourseShell } from "@/components/CourseShell";
import { LessonView } from "@/components/LessonView";
import { getLesson, getModule } from "@/data/lessons";

export const Route = createFileRoute(
  "/courses/business-and-accountancy/basic-business-accountancy_/lessons/$lessonId",
)({
  // Auth gating happens client-side inside <LessonView> via a useEffect.
  // We deliberately do NOT run a session check in beforeLoad: that would run
  // during SSR with no cookie/localStorage session available and incorrectly
  // redirect signed-in users to "/" on a hard refresh. The Mux signed
  // playback token is server-issued and fails closed for unauth users, so
  // lesson content is still protected.
  head: ({ params }) => {
    const lesson = getLesson(params.lessonId);
    if (!lesson) {
      return { meta: [{ title: "Lesson — WHO KNEW" }] };
    }
    const mod = getModule(lesson.moduleNumber);
    const title = `Lesson ${lesson.id}: ${lesson.title} — WHO KNEW`;
    const description = `Lesson ${lesson.id} of The Basic Business Accountancy Course. ${lesson.title}.${
      mod ? ` Part of Module ${mod.number} — ${mod.title}.` : ""
    }`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: `/courses/business-and-accountancy/basic-business-accountancy/lessons/${lesson.id}` },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center bg-paper p-8">
      <div className="text-center">
        <h1 className="text-[32px] font-bold text-ink">Lesson not found</h1>
        <p className="mt-3 text-[16px] italic" style={{ color: "#5A534A" }}>
          That lesson doesn't exist.
        </p>
      </div>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen flex items-center justify-center bg-paper p-8">
      <div className="text-center max-w-md">
        <h1 className="text-[24px] font-bold text-ink">Something went wrong</h1>
        <p className="mt-3 text-[14px]" style={{ color: "#5A534A" }}>
          {error.message}
        </p>
      </div>
    </div>
  ),
  component: LessonPage,
});

function LessonPage() {
  // Lesson data is static (src/data/lessons.ts), so we derive it from the
  // route params instead of a loader: the generated route registry does not
  // carry loader types, which collapsed useLoaderData() to `undefined` and
  // broke the typecheck.
  const { lessonId } = Route.useParams();
  const lesson = getLesson(lessonId);
  if (!lesson) throw notFound();
  return (
    <CourseShell activeLessonId={lesson.id}>
      <LessonView lesson={lesson} />
    </CourseShell>
  );
}
